import { authenticatedFetch } from "./http";

export interface RepackagingTask {
  id: string;
  repackagingNumber: string;
  handlingUnitId: string;
  huNumber: string;
  asnNumber: string | null;
  receiptNumber: string | null;
  clientCode: string | null;
  clientName: string | null;
  itemId: string;
  itemCode: string;
  serialNumbers: string | null;
  quantity: number;
  uom: string;
  warehouseId: string;
  warehouseCode: string;
  processPointCode: string;
  reasonCode: string;
  reportedComment: string;
  originalItemPackagingId: string | null;
  originalPackagingCode: string | null;
  targetItemPackagingId: string | null;
  targetPackagingCode: string | null;
  status: "WAITING_TRANSFER" | "OPEN" | "IN_PROGRESS" | "EXCEPTION" |
    "COMPLETED" | "CANCELLED";
  assignedUserId: string | null;
  assignedUserName: string | null;
  completionComment: string | null;
  exceptionCode: string | null;
  exceptionComment: string | null;
  exceptionReportedAt: string | null;
  exceptionReportedByName: string | null;
  resolutionAction: string | null;
  resolutionComment: string | null;
  resolvedAt: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
}

export interface RepackagingOption {
  itemPackagingId: string;
  packagingCode: string;
  displayName: string;
  packagingType: string;
  packagingLevel: string;
  uom: string;
  baseQuantity: number;
  lengthMm: number;
  widthMm: number;
  heightMm: number;
  grossWeightKg: number;
}

export function loadRepackagingTasks(): Promise<RepackagingTask[]> {
  return authenticatedFetch<RepackagingTask[]>("/api/v1/repackaging/tasks");
}

export function loadRepackagingTask(id: string): Promise<RepackagingTask> {
  return authenticatedFetch<RepackagingTask>(`/api/v1/repackaging/tasks/${id}`);
}

export function loadRepackagingOptions(id: string): Promise<RepackagingOption[]> {
  return authenticatedFetch<RepackagingOption[]>(
    `/api/v1/repackaging/tasks/${id}/options`
  );
}

export function claimRepackagingTask(
  task: RepackagingTask
): Promise<RepackagingTask> {
  return authenticatedFetch<RepackagingTask>(
    `/api/v1/repackaging/tasks/${task.id}/claim`,
    { method: "POST", body: JSON.stringify({ version: task.version }) }
  );
}

export function completeRepackagingTask(
  task: RepackagingTask,
  targetItemPackagingId: string,
  departureProcessPointId: string,
  comment: string
): Promise<RepackagingTask> {
  return authenticatedFetch<RepackagingTask>(
    `/api/v1/repackaging/tasks/${task.id}/complete`,
    {
      method: "POST",
      body: JSON.stringify({
        version: task.version,
        targetItemPackagingId,
        departureProcessPointId,
        comment
      })
    }
  );
}

export function reportRepackagingException(
  task: RepackagingTask, code: string, comment: string
): Promise<RepackagingTask> {
  return authenticatedFetch<RepackagingTask>(
    `/api/v1/repackaging/tasks/${task.id}/exception`,
    {
      method: "POST",
      body: JSON.stringify({ code, comment, version: task.version })
    }
  );
}

export function resolveRepackagingException(
  task: RepackagingTask, action: string, comment: string
): Promise<RepackagingTask> {
  return authenticatedFetch<RepackagingTask>(
    `/api/v1/repackaging/tasks/${task.id}/exception/resolve`,
    {
      method: "POST",
      body: JSON.stringify({ action, comment, version: task.version })
    }
  );
}
